import { buildAdjacency, computeStats } from "./geometry.ts";
import { asymmetryOf, extentsOf, findWaist, type Shape, widthsOf } from "./judge.ts";

/**
 * How much a piece has each of the shapes its labels name (see tags.ts), measured on its
 * mesh: each is only worth comparing with the same measure of other pieces. The clay's
 * volume is always the same, so none needs its size taken out.
 */
export interface FormMeasures {
  /** Sphericity: a ball's surface over the piece's, 1 for a ball. */
  round: number;
  /** Log of the longest principal extent over the mean of the other two. */
  long: number;
  /** Wide and low, round and bottom-heavy (see measureForms). */
  stout: number;
  /** Tall for its widest span, and thin. */
  lanky: number;
  /** Depth of the deepest neck, upright or along the longest axis (see findWaist). */
  waist: number;
  /** Sharp tips, and how sharp (see tipsOf). */
  spiky: number;
  /** Symmetric left to right as it is not otherwise (see measureForms). */
  symmetric: number;
  /** Spread thin as a sheet, beyond what rods and walls are. */
  frilly: number;
  /** Share of the surface that is flat, or bent one way only (see unbentOf). */
  straight: number;
  /** Cross-sections turning steadily one way along the longest axis (see twistOf). */
  twisted: number;
}

/** Slices along an axis, as the judge's width profile has them. */
const SLICES = 48, DIRECTIONS = 8;
/**
 * Slices are taken together until they are this thick: thinner, some would catch only a few
 * of the mesh's vertices and seem narrow.
 */
const SLAB = 0.1;
/** A tip is the farthest point from the centroid within this reach around it. */
const TIP_REACH = 0.25;
/** Tips sharper than this count, by how much sharper: a rounded end falls away more slowly. */
const TIP_SHARP = 0.5;
/** Gaussian curvature is taken over patches this far across… */
const PATCH_REACH = 0.2;
/** …and a patch below this much counts as unbent (a unit ball is 1 all over). */
const UNBENT_BELOW = 0.2;
/** Points the patches are taken around, at most, spread over the mesh's vertices. */
const PATCHES = 2000;
/** A sheet is spread more than this many times as wide as it is thick (see frilly). */
const SHEET_SPREAD = 12;
/** Mirror distances this small are all but equal (meshing noise). */
const MIRROR_NOISE = 0.02;
/** Slices whose sections turn along the longest axis (see twistOf). */
const TWIST_SLICES = 24;

/** Eigenvectors of a symmetric 3×3 matrix (Jacobi rotations), as columns' rows. */
function eigenvectors(matrix: number[][]): number[][] {
  const a = matrix.map((row) => row.slice());
  const v = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let sweep = 0; sweep < 50; sweep++) {
    if (a[0][1] ** 2 + a[0][2] ** 2 + a[1][2] ** 2 < 1e-18) break;
    for (let p = 0; p < 3; p++) {
      for (let q = p + 1; q < 3; q++) {
        if (Math.abs(a[p][q]) < 1e-15) continue;
        const theta = (a[q][q] - a[p][p]) / (2 * a[p][q]);
        const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1), s = t * c;
        for (let k = 0; k < 3; k++) {
          [a[k][p], a[k][q]] = [c * a[k][p] - s * a[k][q], s * a[k][p] + c * a[k][q]];
        }
        for (let k = 0; k < 3; k++) {
          [a[p][k], a[q][k]] = [c * a[p][k] - s * a[q][k], s * a[p][k] + c * a[q][k]];
        }
        for (let k = 0; k < 3; k++) {
          [v[k][p], v[k][q]] = [c * v[k][p] - s * v[k][q], s * v[k][p] + c * v[k][q]];
        }
      }
    }
  }
  return [0, 1, 2].map((j) => v.map((row) => row[j]));
}

/** Extents along the principal axes of the surface, longest first. */
function principalExtents({ positions: p, areas, centroid }: Shape): number[] {
  const c = centroid.toArray();
  const m = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  for (let i = 0; i < areas.length; i++) {
    const d = [p[i * 3] - c[0], p[i * 3 + 1] - c[1], p[i * 3 + 2] - c[2]];
    for (let a = 0; a < 3; a++) for (let b = 0; b < 3; b++) m[a][b] += areas[i] * d[a] * d[b];
  }
  return eigenvectors(m).map((e) => {
    let lo = Infinity, hi = -Infinity;
    for (let i = 0; i < areas.length; i++) {
      const d = (p[i * 3] - c[0]) * e[0] + (p[i * 3 + 1] - c[1]) * e[1] +
        (p[i * 3 + 2] - c[2]) * e[2];
      lo = Math.min(lo, d);
      hi = Math.max(hi, d);
    }
    return hi - lo;
  }).sort((a, b) => b - a);
}

/**
 * Sharp tips and how sharp: each point farthest from the centroid within TIP_REACH of it
 * (on the surface), by how steeply the surface falls away from it, out to that reach, in
 * distance from the centroid per distance along. A cone's tip falls away at nearly 1; a
 * rounded end hardly at first.
 */
function tipsOf({ positions: p, index, centroid }: Shape): number[] {
  const n = p.length / 3, c = centroid.toArray();
  const r = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    r[i] = Math.hypot(p[i * 3] - c[0], p[i * 3 + 1] - c[1], p[i * 3 + 2] - c[2]);
  }
  const { offsets, neighbors } = buildAdjacency(index, n);
  const seen = new Int32Array(n).fill(-1);
  const tips: number[] = [];
  for (let i = 0; i < n; i++) {
    // Of points as far out, the first counts (a symmetric mesh has them in rings).
    const above = (q: number) => r[q] > r[i] || (r[q] === r[i] && q < i);
    let top = true;
    for (let k = offsets[i]; k < offsets[i + 1]; k++) if (above(neighbors[k])) top = false;
    if (!top) continue;
    const stack = [i];
    seen[i] = i;
    let far = 0, along = 0, count = 0, highest = true;
    while (stack.length && highest) {
      const j = stack.pop()!;
      for (let k = offsets[j]; k < offsets[j + 1]; k++) {
        const q = neighbors[k];
        if (seen[q] === i) continue;
        seen[q] = i;
        const d = Math.hypot(
          p[q * 3] - p[i * 3],
          p[q * 3 + 1] - p[i * 3 + 1],
          p[q * 3 + 2] - p[i * 3 + 2],
        );
        if (d > TIP_REACH) continue;
        if (above(q)) highest = false;
        if (d > 0.6 * TIP_REACH) {
          far += r[q];
          along += d;
          count++;
        }
        stack.push(q);
      }
    }
    if (highest && count) tips.push((r[i] - far / count) / (along / count));
  }
  return tips;
}

/**
 * Share of the surface that bends one way at most (flat, or curved as a cylinder or a cone
 * is): the Gaussian curvature over a patch PATCH_REACH across around each point, from the
 * patch's angle defect (so the mesh's own noise sums away), near 0. The share is found over
 * PATCHES of the points, which is as good and much quicker.
 */
function unbentOf({ positions: p, index, areas }: Shape): number {
  const n = p.length / 3;
  const angle = new Float64Array(n);
  for (let t = 0; t < index.length; t += 3) {
    for (let k = 0; k < 3; k++) {
      const a = index[t + k] * 3, b = index[t + (k + 1) % 3] * 3, c = index[t + (k + 2) % 3] * 3;
      const ux = p[b] - p[a], uy = p[b + 1] - p[a + 1], uz = p[b + 2] - p[a + 2];
      const vx = p[c] - p[a], vy = p[c + 1] - p[a + 1], vz = p[c + 2] - p[a + 2];
      angle[index[t + k]] += Math.atan2(
        Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx),
        ux * vx + uy * vy + uz * vz,
      );
    }
  }
  const { offsets, neighbors } = buildAdjacency(index, n);
  const seen = new Int32Array(n).fill(-1);
  let unbent = 0, total = 0;
  for (let i = 0; i < n; i += Math.ceil(n / PATCHES)) {
    const stack = [i];
    seen[i] = i;
    let defect = 2 * Math.PI - angle[i], area = areas[i];
    while (stack.length) {
      const j = stack.pop()!;
      for (let k = offsets[j]; k < offsets[j + 1]; k++) {
        const q = neighbors[k];
        if (seen[q] === i) continue;
        seen[q] = i;
        const d = Math.hypot(
          p[q * 3] - p[i * 3],
          p[q * 3 + 1] - p[i * 3 + 1],
          p[q * 3 + 2] - p[i * 3 + 2],
        );
        if (d > PATCH_REACH) continue;
        defect += 2 * Math.PI - angle[q];
        area += areas[q];
        stack.push(q);
      }
    }
    total += areas[i];
    if (Math.abs(defect / area) < UNBENT_BELOW) unbent += areas[i];
  }
  return unbent / total;
}

/**
 * How far the cross-sections turn one way along `axis`: each slice's section has a long
 * direction (as far as it is longer one way than the other), and the turns from slice to
 * slice, each weighed by how long both are, are summed with their signs. As a share of their
 * sum without signs, it goes towards 0 for sections that turn this way and that (a lumpy
 * piece), and it is the whole turn for a twist.
 */
function twistOf({ positions: p }: Shape, axis: number, from: number, length: number): number {
  const [u, v] = [0, 1, 2].filter((a) => a !== axis);
  const s = Array.from({ length: TWIST_SLICES }, () => [0, 0, 0, 0, 0, 0]);
  for (let i = 0; i < p.length / 3; i++) {
    const k = Math.min(
      TWIST_SLICES - 1,
      Math.floor(((p[i * 3 + axis] - from) / length) * TWIST_SLICES),
    );
    const x = p[i * 3 + u], y = p[i * 3 + v], c = s[k];
    c[0]++;
    c[1] += x;
    c[2] += y;
    c[3] += x * x;
    c[4] += y * y;
    c[5] += x * y;
  }
  const sections = s.map(([n, su, sv, uu, vv, uv]) => {
    if (!n) return null;
    const mu = su / n, mv = sv / n;
    const cuu = uu / n - mu * mu, cvv = vv / n - mv * mv, cuv = uv / n - mu * mv;
    const spread = cuu + cvv;
    return {
      angle: 0.5 * Math.atan2(2 * cuv, cuu - cvv),
      long: spread > 0 ? Math.hypot(cuu - cvv, 2 * cuv) / spread : 0,
    };
  });
  let turn = 0, turned = 0;
  for (let k = 1; k < TWIST_SLICES; k++) {
    const a = sections[k - 1], b = sections[k];
    if (!a || !b) continue;
    let d = b.angle - a.angle;
    if (d > Math.PI / 2) d -= Math.PI;
    if (d < -Math.PI / 2) d += Math.PI;
    const weight = Math.min(a.long, b.long);
    turn += weight * d;
    turned += weight * Math.abs(d);
  }
  return turned ? (turn * turn) / turned : 0;
}

/** Every measure of `shape` (see FormMeasures). */
export function measureForms(shape: Shape): FormMeasures {
  const n = shape.areas.length;
  const { volume, area } = computeStats(
    shape.positions,
    shape.index,
    new Float32Array(n * 3),
    new Float32Array(n),
  );
  const round = Math.cbrt(36 * Math.PI * volume * volume) / area;
  const ext = extentsOf(shape);
  const span = [0, 1, 2].map((a) => ext.max[a] - ext.min[a]);
  const height = span[1], width = (span[0] + span[2]) / 2;
  // Where the centroid stands, from the bottom (0) to the top (1).
  const centroidHeight = -ext.min[1] / height;
  const [l1, l2, l3] = principalExtents(shape);
  // Twice the volume over the area: a sheet's thickness, a rod's radius.
  const thickness = (2 * volume) / area;
  const axis = span.indexOf(Math.max(...span));
  // A neck upright, or along the longest axis: a piece may be wider than it is tall, its waist
  // upright (across it, a handle would be taken for a neck). A slab's width is across it as a
  // whole, not the narrowest way, which would find a neck at a thin handle.
  const waist = Math.max(...[1, axis].map((a) => {
    const widths = widthsOf(shape, a), together = Math.ceil((SLAB * SLICES) / span[a]);
    const slabs: number[] = [];
    for (let k = 0; k < SLICES; k++) {
      let sum = 0;
      for (let j = 0; j < DIRECTIONS; j++) sum += widths[k * DIRECTIONS + j];
      const slab = Math.floor(k / together);
      slabs[slab] = Math.max(slabs[slab] ?? 0, sum / DIRECTIONS);
    }
    return findWaist(slabs).depth;
  }));
  const mirrored = [0, 1, 2].map((a) => asymmetryOf(shape, a));
  const from = ext.min[axis] + shape.centroid.getComponent(axis);
  return {
    round,
    long: Math.log(l1 / Math.sqrt(l2 * l3)),
    stout: Math.log(width / height) + 2 * Math.log(round) - (centroidHeight - 0.5),
    lanky: Math.log(height / Math.max(span[0], span[2])) - 0.5 * Math.log(round),
    waist,
    spiky: tipsOf(shape).reduce((sum, s) => sum + Math.max(0, s - TIP_SHARP), 0),
    // A ball is as symmetric every way, so only a piece that is not so up and down or front
    // to back counts, as far as it is left to right.
    symmetric: Math.max(
      0,
      1 - (mirrored[0] + MIRROR_NOISE) / (Math.max(mirrored[1], mirrored[2]) + MIRROR_NOISE),
    ),
    frilly: Math.max(0, Math.log(l2 / (SHEET_SPREAD * thickness))),
    straight: unbentOf(shape),
    twisted: twistOf(shape, axis, from, span[axis]),
  };
}
